import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
  within,
} from "@testing-library/react";
import "@testing-library/jest-dom";

import {
  areChatMessagePropsEqual,
  FullPageChatLayout,
} from "./FullPageChatLayout";
import { AgentGemstone } from "./AgentGemstone";
import { AGENT_AVATAR_CUTS } from "./agentAvatarIdentity";
import type {
  CollaborationConversationSummary,
  ConversationCollaborator,
} from "../services/collaboration";
import { normalizeConversationSummary } from "../services/collaboration";
import { useAuthFetch } from "../../../utils/useAuthFetch";

jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  AttachmentPicker: ({
    attachments,
  }: {
    attachments: { fileName: string }[];
  }) => (
    <div>
      {attachments.map((attachment) => (
        <span key={attachment.fileName}>{attachment.fileName}</span>
      ))}
    </div>
  ),
  ChatMessage: ({
    message,
    extensions,
    icons,
  }: {
    message: any;
    icons?: {
      user?: React.ReactNode;
      assistant?: React.ReactNode;
      tool?: React.ReactNode;
    };
    extensions?: {
      renderHeader?: (message: any) => React.ReactNode;
      renderContent?: (
        message: any,
        content: React.ReactNode,
      ) => React.ReactNode;
      renderActions?: (message: any) => React.ReactNode;
    };
  }) => {
    const defaultRoleIcon =
      message.role === "user"
        ? "U"
        : message.role === "assistant"
          ? "A"
          : message.role === "tool"
            ? "T"
            : null;
    const configuredRoleIcon =
      message.role === "user"
        ? icons?.user
        : message.role === "assistant"
          ? icons?.assistant
          : message.role === "tool"
            ? icons?.tool
            : undefined;
    const roleIcon =
      configuredRoleIcon === undefined ? defaultRoleIcon : configuredRoleIcon;
    const avatarTestId =
      roleIcon && typeof roleIcon === "object"
        ? `agent-message-avatar-slot-${message.id}`
        : `generic-role-avatar-${message.id}`;
    return (
      <div
        className={`mmagent-chat-message mmagent-chat-message--${message.role}`}
        data-testid={`chat-message-${message.id}`}
        data-presented-role={message.role}
      >
        {roleIcon !== null && roleIcon !== undefined && (
          <span
            className="mmagent-chat-message__avatar"
            data-testid={avatarTestId}
          >
            {roleIcon}
          </span>
        )}
        <div className="mmagent-chat-message__body">
          {extensions?.renderHeader?.(message)}
          <div
            className="mmagent-chat-message__content"
            data-testid={`chat-message-content-${message.id}`}
          >
            {extensions?.renderContent?.(message, message.content) ??
              message.content}
            {message.toolExecutions?.map(
              (execution: { id: string; name: string }) => (
                <div
                  className="mmagent-chat-message__tool-inline"
                  data-testid={`chat-message-tool-inline-${message.id}-${execution.id}`}
                  key={execution.id}
                >
                  <a href={`#${execution.id}`}>{execution.name} result</a>
                  <code>{execution.name}</code>
                </div>
              ),
            )}
          </div>
          {extensions?.renderActions?.(message)}
        </div>
      </div>
    );
  },
  JobProgressIndicator: () => null,
  useChatStore: (selector: (state: any) => any) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: jest.fn(),
}));

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-42" } }),
}));

const mockUseAuthFetch = useAuthFetch as jest.Mock;
const mockAuthFetch = jest.fn();

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:test-url"),
  });
  Object.defineProperty(URL, "revokeObjectURL", {
    configurable: true,
    value: jest.fn(),
  });
});

const currentUser = {
  id: "collab-self",
  name: "Bob Example",
  email: "bob@example.com",
  role: "user",
  status: "accepted",
  role_in_org: "member",
};

const workspaceUsers = [
  {
    user_id: "owner-1",
    email: "alice@example.com",
    role: "member",
    status: "accepted",
  },
  {
    user_id: "collab-self",
    email: "bob@example.com",
    role: "member",
    status: "accepted",
  },
  {
    user_id: "collab-2",
    email: "charlie@example.com",
    role: "member",
    status: "accepted",
  },
  {
    user_id: "collab-3",
    email: "dana@example.com",
    role: "member",
    status: "accepted",
  },
];

const baseConversations: CollaborationConversationSummary[] = [
  {
    id: 1,
    externalId: null,
    title: "Shared chat",
    kind: "agent",
    agentName: "Vivid Otter",
    systemPromptOverride: null,
    agentStatus: "idle",
    agentLastHeartbeatAt: null,
    agentCurrentTask: null,
    messageCount: 4,
    lastMessageAt: "2026-04-25T10:05:00Z",
    isArchived: false,
    decommissionedAt: null,
    createdAt: "2026-04-25T10:00:00Z",
    updatedAt: "2026-04-25T10:05:00Z",
    userRole: "collaborator",
    archivedReason: null,
    avatar: {
      status: "assigned",
      cut: "rhomboid",
      colour: "rose",
      reservationActive: true,
      recoveryRequired: false,
    },
  },
];

const ownerAndSelf: ConversationCollaborator[] = [
  {
    user_id: "owner-1",
    display_name: "alice@example.com",
    email: "alice@example.com",
    role: "owner",
    added_at: "2026-04-25T10:00:00Z",
    added_by: null,
  },
  {
    user_id: "collab-self",
    display_name: "bob@example.com",
    email: "bob@example.com",
    role: "collaborator",
    added_at: "2026-04-25T10:01:00Z",
    added_by: "alice@example.com",
  },
];

const renderLayout = (
  overrides: Partial<React.ComponentProps<typeof FullPageChatLayout>> = {},
) =>
  render(
    <FullPageChatLayout
      messages={[]}
      onSendMessage={jest.fn()}
      isLoading={false}
      conversations={baseConversations}
      onLoadConversation={jest.fn()}
      onNewConversation={jest.fn()}
      onSearchConversations={jest.fn()}
      onLoadMoreConversations={jest.fn()}
      conversationsLoading={false}
      currentConversationId={1}
      collaborationEnabled={true}
      workspaceRole="owner"
      currentUser={currentUser as any}
      workspaceUsers={workspaceUsers as any}
      currentConversationCollaborators={ownerAndSelf}
      conversationCollaboratorsById={{ 1: ownerAndSelf }}
      onGetAgentAvatarReservations={jest
        .fn()
        .mockResolvedValue({ reservedCuts: [], reservedColours: [] })}
      {...overrides}
    />,
  );

describe("FullPageChatLayout collaboration UI", () => {
  beforeEach(() => {
    jest.clearAllMocks();
    mockUseAuthFetch.mockReturnValue(mockAuthFetch);
  });

  it("uses Agent labels when workspace mode is agent", () => {
    renderLayout({
      workspaceMode: "agent",
      conversations: [],
      currentConversationId: null,
    });

    expect(
      screen.getAllByRole("button", { name: /new agent/i })[0],
    ).toBeInTheDocument();
    expect(screen.getByTestId("agent-inventory-bar")).toBeInTheDocument();
    expect(
      screen.queryByPlaceholderText("Search agents..."),
    ).not.toBeInTheDocument();
    expect(screen.queryByText("Agent inventory")).not.toBeInTheDocument();
    expect(
      screen.queryByTestId("conversation-history-button"),
    ).not.toBeInTheDocument();
    expect(screen.queryByText("Conversations")).not.toBeInTheDocument();
  });

  it("shows New Agent to a workspace owner", () => {
    renderLayout({
      workspaceMode: "agent",
      conversations: [],
      currentConversationId: null,
      workspaceRole: "owner",
    });

    expect(screen.getByTestId("agent-inventory-new-agent")).toBeInTheDocument();
    expect(screen.getAllByRole("button", { name: "New Agent" })).toHaveLength(
      2,
    );
  });

  it("opens the delete confirmation when the message trash action is clicked", async () => {
    const onDeleteMessagesFrom = jest.fn().mockResolvedValue(undefined);
    renderLayout({
      messages: [
        {
          id: "delete-confirmation-target",
          role: "user",
          content: "test message",
          createdAt: new Date("2026-10-26T12:00:00Z"),
        },
      ] as any,
      onDeleteMessagesFrom,
    });

    fireEvent.click(screen.getByTitle("Delete message"));

    expect(
      await screen.findByRole("alertdialog", { name: "Delete message?" }),
    ).toBeInTheDocument();
    expect(onDeleteMessagesFrom).not.toHaveBeenCalled();
  });

  it("hides agent creation from a workspace user but keeps existing agents usable", () => {
    const onLoadConversation = jest.fn();
    const onSendMessage = jest.fn();
    renderLayout({
      workspaceMode: "agent",
      workspaceRole: "user",
      conversations: baseConversations,
      currentConversationId: 1,
      onLoadConversation,
      onSendMessage,
    });

    expect(
      screen.queryByTestId("agent-inventory-new-agent"),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "New Agent" }),
    ).not.toBeInTheDocument();

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1"));
    expect(onLoadConversation).toHaveBeenCalledWith(1);

    fireEvent.change(screen.getByRole("textbox"), {
      target: { value: "Send a message to the existing agent" },
    });
    fireEvent.click(screen.getByRole("button", { name: "Send message" }));
    expect(onSendMessage).toHaveBeenCalledWith(
      "Send a message to the existing agent",
    );
  });

  it("disables the composer immediately after New Agent starts creation", () => {
    const CreationGuardHarness = () => {
      const [agentCreationPending, setAgentCreationPending] =
        React.useState(false);
      return (
        <FullPageChatLayout
          messages={[]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={[]}
          onLoadConversation={jest.fn()}
          onNewConversation={() => setAgentCreationPending(true)}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={jest.fn()}
          conversationsLoading={false}
          currentConversationId={null}
          workspaceMode="agent"
          workspaceRole="owner"
          agentCreationPending={agentCreationPending}
        />
      );
    };

    render(<CreationGuardHarness />);
    expect(screen.queryByRole("textbox")).not.toBeInTheDocument();
    expect(
      screen.getByText(
        "Create your first agent to start working in this workspace.",
      ),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByTestId("agent-inventory-new-agent"));
    expect(screen.getByRole("textbox")).toBeDisabled();
  });

  it("disables the agent inventory New Agent control for the bounded creation delay", () => {
    const AgentCreationGuardHarness = ({
      agentCreationPending,
    }: {
      agentCreationPending: boolean;
    }) => {
      return (
        <FullPageChatLayout
          messages={[]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={[]}
          onLoadConversation={jest.fn()}
          onNewConversation={jest.fn()}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={jest.fn()}
          conversationsLoading={false}
          currentConversationId={null}
          workspaceMode="agent"
          workspaceRole="owner"
          agentCreationPending={agentCreationPending}
        />
      );
    };

    const { rerender } = render(
      <AgentCreationGuardHarness agentCreationPending={false} />,
    );
    expect(screen.getByTestId("agent-inventory-new-agent")).not.toBeDisabled();

    rerender(<AgentCreationGuardHarness agentCreationPending={true} />);
    expect(screen.getByTestId("agent-inventory-new-agent")).toBeDisabled();

    rerender(<AgentCreationGuardHarness agentCreationPending={false} />);
    expect(screen.getByTestId("agent-inventory-new-agent")).not.toBeDisabled();
  });

  it("disables the chat drawer New Conversation control while switching", () => {
    renderLayout({ workspaceMode: "chat", conversationSwitching: true });

    fireEvent.click(screen.getByTestId("conversation-history-button"));

    expect(
      screen.getByRole("button", { name: /new conversation/i }),
    ).toHaveAttribute("aria-disabled", "true");
  });

  it("keeps transport readiness internal instead of rendering a status banner", () => {
    const { rerender } = renderLayout({
      workspaceMode: "agent",
      agentStatusPushActive: false,
    });

    expect(screen.getByTestId("agent-inventory-bar")).toHaveAttribute(
      "data-agent-status-push-active",
      "false",
    );
    expect(
      screen.queryByTestId("agent-status-push-availability"),
    ).not.toBeInTheDocument();

    rerender(
      <FullPageChatLayout
        messages={[]}
        onSendMessage={jest.fn()}
        isLoading={false}
        conversations={baseConversations}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={1}
        workspaceMode="agent"
        agentStatusPushActive={true}
      />,
    );

    expect(screen.getByTestId("agent-inventory-bar")).toHaveAttribute(
      "data-agent-status-push-active",
      "true",
    );
    expect(
      screen.queryByText(/live status|refreshing periodically/i),
    ).not.toBeInTheDocument();
  });

  it("opens the agent persona editor from the inventory arrow menu", async () => {
    const onUpdateAgentConversation = jest.fn().mockResolvedValue(undefined);
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation,
    });

    expect(screen.getByTestId("agent-gemstone-1")).toHaveAttribute(
      "data-gemstone-cut",
      "rhomboid",
    );
    expect(screen.getByTestId("agent-inventory-agent-1")).toHaveAccessibleName(
      "Open Vivid Otter",
    );
    expect(
      screen.queryByTestId("selected-agent-chat-header"),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByText(/^(working|finished|idle)$/i),
    ).not.toBeInTheDocument();

    fireEvent.mouseEnter(screen.getByTestId("agent-inventory-agent-1"));
    expect(
      await screen.findByTestId("agent-inventory-agent-1-popover"),
    ).toHaveTextContent("Vivid Otter");
    expect(
      screen.queryByRole("menuitem", { name: /edit persona/i }),
    ).not.toBeInTheDocument();

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    expect(
      screen.queryByRole("menuitem", { name: /send from current agent/i }),
    ).not.toBeInTheDocument();
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );

    fireEvent.change(screen.getByLabelText("Agent name"), {
      target: { value: "Steady Lynx" },
    });
    fireEvent.change(screen.getByLabelText("Agent persona instructions"), {
      target: { value: "Focus on risk." },
    });
    fireEvent.click(screen.getByRole("button", { name: /save persona/i }));

    await waitFor(() =>
      expect(onUpdateAgentConversation).toHaveBeenCalledWith(1, {
        agentName: "Steady Lynx",
        systemPromptOverride: "Focus on risk.",
      }),
    );
  });

  it("shows the agent action menu to workspace owner", async () => {
    const conversation = baseConversations[0];
    renderLayout({
      workspaceMode: "agent",
      conversations: [{ ...conversation, userRole: "owner" }],
      workspaceRole: "owner",
    });

    expect(
      screen.getByTestId("agent-inventory-agent-1-menu-trigger"),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    expect(
      await screen.findByRole("menuitem", { name: "Edit persona" }),
    ).toBeInTheDocument();
    expect(
      screen.getByRole("menuitem", { name: "Change appearance" }),
    ).toBeInTheDocument();
    expect(
      screen.getByRole("menuitem", { name: "Delete agent" }),
    ).toBeInTheDocument();
  });

  it("labels and opens the marked setup agent only while workspace setup is needed", () => {
    const onLoadConversation = jest.fn();
    const { unmount } = renderLayout({
      workspaceMode: "agent",
      needsOnboarding: true,
      onboardingAgentId: 1,
      onLoadConversation,
    });

    const continueButton = screen.getByRole("button", {
      name: "Continue workspace setup",
    });
    expect(continueButton).toBeInTheDocument();
    fireEvent.click(continueButton);
    expect(onLoadConversation).toHaveBeenCalledWith(1);

    unmount();
    renderLayout({
      workspaceMode: "agent",
      needsOnboarding: false,
      onboardingAgentId: 1,
    });
    expect(
      screen.queryByRole("button", { name: "Continue workspace setup" }),
    ).not.toBeInTheDocument();
  });

  it("shows the agent action menu to workspace admin", async () => {
    const conversation = baseConversations[0];
    renderLayout({
      workspaceMode: "agent",
      conversations: [{ ...conversation, userRole: "owner" }],
      workspaceRole: "admin",
    });
    expect(
      screen.getByTestId("agent-inventory-agent-1-menu-trigger"),
    ).toBeInTheDocument();
    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    expect(
      await screen.findByRole("menuitem", { name: "Edit persona" }),
    ).toBeInTheDocument();
    expect(
      screen.getByRole("menuitem", { name: "Change appearance" }),
    ).toBeInTheDocument();
    expect(
      screen.getByRole("menuitem", { name: "Delete agent" }),
    ).toBeInTheDocument();
  });

  it.each(["user", null])(
    "hides the menu for workspace role %s even when the user owns the agent",
    (workspaceRole) => {
      const conversation = baseConversations[0];
      renderLayout({
        workspaceMode: "agent",
        conversations: [{ ...conversation, userRole: "owner" }],
        workspaceRole,
      });
      expect(
        screen.queryByTestId("agent-inventory-agent-1-menu-trigger"),
      ).not.toBeInTheDocument();
      expect(
        screen.queryByRole("menuitem", { name: "Edit persona" }),
      ).not.toBeInTheDocument();
    },
  );

  it("shows the API conflict message when an agent name is already used", async () => {
    const onUpdateAgentConversation = jest
      .fn()
      .mockRejectedValue(
        new Error(
          "An agent named 'Steady Lynx' is already used in this workspace.",
        ),
      );
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation,
    });

    fireEvent.mouseEnter(screen.getByTestId("agent-inventory-agent-1"));
    await screen.findByTestId("agent-inventory-agent-1-popover");
    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    fireEvent.change(screen.getByLabelText("Agent name"), {
      target: { value: "Steady Lynx" },
    });
    fireEvent.click(screen.getByRole("button", { name: /save persona/i }));

    expect(await screen.findByRole("alert")).toHaveTextContent(
      "already used in this workspace",
    );
    expect(screen.getByText("Edit agent persona")).toBeInTheDocument();
  });

  it("does not request reusable personas without a workspace", async () => {
    renderLayout({
      workspaceMode: "agent",
      onUpdateAgentConversation: jest.fn().mockResolvedValue(undefined),
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    fireEvent.change(screen.getByLabelText("Agent persona instructions"), {
      target: { value: "A reusable instruction." },
    });
    fireEvent.change(screen.getByPlaceholderText("Persona name"), {
      target: { value: "No workspace" },
    });
    fireEvent.click(screen.getByRole("button", { name: /^save$/i }));

    expect(mockAuthFetch).not.toHaveBeenCalled();
  });

  it("applies a reusable persona by identifier, not prompt text", async () => {
    const onUpdateAgentConversation = jest.fn().mockResolvedValue(undefined);
    mockAuthFetch.mockResolvedValueOnce({
      own: [
        {
          id: "persona-42",
          name: "Risk reviewer",
          body: "Challenge assumptions before proposing a decision.",
          is_shared: false,
          version: 1,
          owner_user_id: "collab-self",
          updated_at: "2026-09-08T10:00:00Z",
          is_update_available: false,
        },
      ],
      shared: [],
    });
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation,
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    fireEvent.click(
      await screen.findByRole("button", { name: /risk reviewer v1.*load/i }),
    );

    await waitFor(() =>
      expect(onUpdateAgentConversation).toHaveBeenCalledWith(1, {
        personaSourceId: "persona-42",
      }),
    );
    await waitFor(() =>
      expect(screen.getByLabelText("Agent name")).toHaveValue("Risk reviewer"),
    );
    expect(screen.getByLabelText("Agent persona instructions")).toHaveValue(
      "Challenge assumptions before proposing a decision.",
    );
    expect(onUpdateAgentConversation).not.toHaveBeenCalledWith(
      1,
      expect.objectContaining({
        systemPromptOverride:
          "Challenge assumptions before proposing a decision.",
      }),
    );
  });

  it("shows a recovery error when applying a reusable persona fails", async () => {
    const onUpdateAgentConversation = jest
      .fn()
      .mockRejectedValue(new Error("agent patch failed"));
    mockAuthFetch.mockResolvedValueOnce({
      own: [
        {
          id: "persona-apply-failure",
          name: "Risk reviewer",
          body: "Challenge assumptions.",
          is_shared: false,
          version: 1,
          owner_user_id: "collab-self",
          updated_at: "2026-09-08T10:00:00Z",
          is_update_available: false,
        },
      ],
      shared: [],
    });
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation,
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    fireEvent.click(
      await screen.findByRole("button", { name: /risk reviewer v1.*load/i }),
    );

    expect(
      await screen.findByText(
        "Reusable persona could not be applied. Review the agent and try again.",
      ),
    ).toBeInTheDocument();
    expect(screen.getByRole("dialog")).toBeInTheDocument();
  });

  it("shows a recovery error when the authoritative library refresh after apply fails", async () => {
    const onUpdateAgentConversation = jest.fn().mockResolvedValue(undefined);
    mockAuthFetch
      .mockResolvedValueOnce({
        own: [
          {
            id: "persona-refresh-failure",
            name: "Risk reviewer",
            body: "Challenge assumptions.",
            is_shared: false,
            version: 1,
            owner_user_id: "collab-self",
            updated_at: "2026-09-08T10:00:00Z",
            is_update_available: false,
          },
        ],
        shared: [],
      })
      .mockRejectedValueOnce(new Error("library refresh failed"));
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation,
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    fireEvent.click(
      await screen.findByRole("button", { name: /risk reviewer v1.*load/i }),
    );

    expect(
      await screen.findByText(
        "Reusable persona could not be applied. Review the agent and try again.",
      ),
    ).toBeInTheDocument();
    expect(onUpdateAgentConversation).toHaveBeenCalledWith(1, {
      personaSourceId: "persona-refresh-failure",
    });
    expect(screen.getByRole("dialog")).toBeInTheDocument();
  });

  it("updates, shares, and deletes only an owned reusable persona before reloading the library", async () => {
    const owned = {
      id: "persona-owned",
      name: "Owned reviewer",
      body: "Challenge assumptions.",
      is_shared: false,
      version: 1,
      owner_user_id: "collab-self",
      updated_at: "2026-09-08T10:00:00Z",
      is_update_available: false,
    };
    const shared = {
      ...owned,
      id: "persona-shared",
      name: "Shared reviewer",
      owner_user_id: "another-user",
      is_shared: true,
    };
    mockAuthFetch
      .mockResolvedValueOnce({ own: [owned], shared: [shared] })
      .mockResolvedValueOnce({
        ...owned,
        name: "Owned reviewer v2",
        body: "Explain trade-offs.",
        is_shared: true,
        version: 2,
      })
      .mockResolvedValueOnce({
        own: [
          {
            ...owned,
            name: "Owned reviewer v2",
            body: "Explain trade-offs.",
            is_shared: true,
            version: 2,
          },
        ],
        shared: [shared],
      })
      .mockResolvedValueOnce(undefined)
      .mockResolvedValueOnce({ own: [], shared: [shared] });
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation: jest.fn().mockResolvedValue(undefined),
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    expect(
      await screen.findByRole("button", { name: /shared reviewer v1 load/i }),
    ).toBeInTheDocument();
    expect(screen.queryAllByRole("button", { name: /^edit$/i })).toHaveLength(
      1,
    );
    expect(screen.queryAllByRole("button", { name: /^delete$/i })).toHaveLength(
      1,
    );

    fireEvent.click(screen.getByRole("button", { name: /^edit$/i }));
    fireEvent.change(screen.getByLabelText("Reusable persona name"), {
      target: { value: "Owned reviewer v2" },
    });
    fireEvent.change(screen.getByLabelText("Reusable persona instructions"), {
      target: { value: "Explain trade-offs." },
    });
    fireEvent.click(
      screen.getByLabelText("Share edited persona with organisation"),
    );
    fireEvent.click(screen.getByRole("button", { name: /save changes/i }));

    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/ida/personas/persona-owned",
        "update reusable persona",
        expect.objectContaining({
          method: "PATCH",
          headers: expect.objectContaining({
            "X-Workspace-Id": "workspace-42",
          }),
          body: JSON.stringify({
            name: "Owned reviewer v2",
            body: "Explain trade-offs.",
            is_shared: true,
          }),
        }),
      ),
    );
    expect(mockAuthFetch).toHaveBeenCalledWith(
      "/api/workspace/ida/personas?agent_conversation_id=1",
      "fetch reusable personas",
      { headers: { "X-Workspace-Id": "workspace-42" } },
    );

    fireEvent.click(await screen.findByRole("button", { name: /^delete$/i }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/ida/personas/persona-owned",
        "delete reusable persona",
        expect.objectContaining({ method: "DELETE" }),
      ),
    );
    expect(
      screen.queryByRole("button", { name: /owned reviewer v2/i }),
    ).not.toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: /shared reviewer v1 load/i }),
    ).toBeInTheDocument();
  });

  it("uses a full owner payload for the post-create sharing toggle and reloads", async () => {
    const persona = {
      id: "persona-toggle",
      name: "Toggle reviewer",
      body: "Keep findings concise.",
      is_shared: false,
      version: 1,
      owner_user_id: "collab-self",
      updated_at: "2026-09-08T10:00:00Z",
      is_update_available: false,
    };
    mockAuthFetch
      .mockResolvedValueOnce({ own: [persona], shared: [] })
      .mockResolvedValueOnce({ ...persona, is_shared: true, version: 2 })
      .mockResolvedValueOnce({
        own: [{ ...persona, is_shared: true, version: 2 }],
        shared: [],
      });
    renderLayout({
      workspaceMode: "agent",
      workspaceId: "workspace-42",
      onUpdateAgentConversation: jest.fn().mockResolvedValue(undefined),
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    );
    fireEvent.click(
      await screen.findByLabelText("Share Toggle reviewer with organisation"),
    );

    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/ida/personas/persona-toggle",
        "update reusable persona",
        expect.objectContaining({
          method: "PATCH",
          body: JSON.stringify({
            name: "Toggle reviewer",
            body: "Keep findings concise.",
            is_shared: true,
          }),
        }),
      ),
    );
    await waitFor(() =>
      expect(
        screen.getByLabelText("Share Toggle reviewer with organisation"),
      ).toBeChecked(),
    );
  });

  it("replaces the avatar aria-label after agent rename", () => {
    const { rerender } = renderLayout({
      workspaceMode: "agent",
      conversations: [
        {
          ...baseConversations[0],
          agentName: "Lucid Lynx",
        },
      ],
    });

    expect(screen.getByTestId("agent-inventory-agent-1")).toHaveAccessibleName(
      "Open Lucid Lynx",
    );

    rerender(
      <FullPageChatLayout
        messages={[]}
        onSendMessage={jest.fn()}
        isLoading={false}
        conversations={[
          {
            ...baseConversations[0],
            agentName: "Quiet Otter",
          },
        ]}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={1}
        collaborationEnabled={true}
        currentUser={currentUser as any}
        workspaceUsers={workspaceUsers as any}
        currentConversationCollaborators={ownerAndSelf}
        conversationCollaboratorsById={{ 1: ownerAndSelf }}
        workspaceMode="agent"
      />,
    );

    const avatar = screen.getByTestId("agent-inventory-agent-1");
    expect(screen.getByTestId("agent-gemstone-1")).toHaveAttribute(
      "role",
      "img",
    );
    expect(avatar).toHaveAccessibleName("Open Quiet Otter");
    expect(avatar).not.toHaveAccessibleName("Open Lucid LynxQuiet Otter");
  });

  it("toggles the agent arrow menu closed on re-click", async () => {
    renderLayout({
      workspaceMode: "agent",
    });

    const trigger = screen.getByTestId("agent-inventory-agent-1-menu-trigger");
    fireEvent.click(trigger);
    expect(
      await screen.findByRole("menuitem", { name: /edit persona/i }),
    ).toBeInTheDocument();

    fireEvent.click(trigger);
    await waitFor(() =>
      expect(
        screen.queryByRole("menuitem", { name: /edit persona/i }),
      ).not.toBeInTheDocument(),
    );
  });

  it("keeps the composer pinned outside the chat history scroll container", () => {
    renderLayout({
      messages: [
        {
          id: "message-1",
          role: "assistant",
          content: "Reply",
          createdAt: new Date("2026-04-25T10:00:00Z"),
        },
      ] as any,
    });

    expect(screen.getByTestId("chat-scroll-container")).toHaveStyle({
      overflowY: "auto",
      minHeight: "0",
    });
    expect(screen.getByTestId("chat-header-container")).toHaveStyle({
      position: "fixed",
    });
    expect(screen.getByTestId("chat-composer-container")).toHaveStyle({
      position: "fixed",
      bottom: "0px",
    });
  });

  it("measures the composer when an agent conversation becomes active", () => {
    const bounds = jest
      .spyOn(HTMLElement.prototype, "getBoundingClientRect")
      .mockImplementation(function (this: HTMLElement) {
        return {
          height: this.dataset.testid === "chat-composer-container" ? 120 : 0,
        } as DOMRect;
      });

    try {
      const view = renderLayout({
        workspaceMode: "agent",
        conversations: baseConversations,
        currentConversationId: null,
      });
      expect(
        screen.queryByTestId("chat-composer-container"),
      ).not.toBeInTheDocument();

      view.rerender(
        <FullPageChatLayout
          messages={[]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={baseConversations}
          onLoadConversation={jest.fn()}
          onNewConversation={jest.fn()}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={jest.fn()}
          conversationsLoading={false}
          currentConversationId={1}
          workspaceMode="agent"
        />,
      );

      expect(screen.getByTestId("chat-composer-container")).toBeInTheDocument();
      expect(screen.getByTestId("chat-scroll-container")).toHaveStyle({
        paddingBottom: "132px",
      });
    } finally {
      bounds.mockRestore();
    }
  });

  it.each(["chat", "agent"] as const)(
    "uses a shrinkable shell in %s mode",
    (workspaceMode) => {
      renderLayout({ workspaceMode });

      const shell = screen.getByTestId("chat-layout-shell");
      expect(shell).toHaveStyle({
        display: "flex",
        flexDirection: "column",
        minHeight: "0",
      });
      expect(shell).not.toHaveStyle({ height: "100%" });
      expect(screen.getByTestId("chat-scroll-container")).toHaveStyle({
        minHeight: "0",
        overflowY: "auto",
      });
    },
  );

  it("pins heartbeat overlay to the viewport instead of the scroll container", () => {
    renderLayout({
      stickyTopContent: <div data-testid="heartbeat-marker">Heartbeat</div>,
    });

    expect(screen.getByTestId("chat-heartbeat-overlay")).toHaveStyle({
      position: "fixed",
    });
    expect(screen.getByTestId("heartbeat-marker")).toBeInTheDocument();
  });

  it("keeps the heartbeat panel below the measured agent strip", () => {
    const bounds = jest
      .spyOn(HTMLElement.prototype, "getBoundingClientRect")
      .mockImplementation(function (this: HTMLElement) {
        return {
          height: this.dataset.testid === "agent-inventory-bar" ? 96 : 0,
        } as DOMRect;
      });
    try {
      renderLayout({
        workspaceMode: "agent",
        stickyTopContent: (
          <div data-testid="heartbeat-marker">Expanded heartbeat</div>
        ),
      });
      expect(screen.getByTestId("chat-heartbeat-overlay")).toHaveStyle({
        top: "164px",
      });
    } finally {
      bounds.mockRestore();
    }
  });

  it("shows the Collaborate button for owners only", () => {
    const { rerender } = renderLayout({
      canManageCollaborators: true,
    });

    expect(screen.getByTestId("collaborate-button")).toBeInTheDocument();

    rerender(
      <FullPageChatLayout
        messages={[]}
        onSendMessage={jest.fn()}
        isLoading={false}
        conversations={baseConversations}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={1}
        collaborationEnabled={true}
        canManageCollaborators={false}
        currentUser={currentUser as any}
        workspaceUsers={workspaceUsers as any}
        currentConversationCollaborators={ownerAndSelf}
        conversationCollaboratorsById={{ 1: ownerAndSelf }}
      />,
    );

    expect(screen.queryByTestId("collaborate-button")).not.toBeInTheDocument();
  });

  it("hides the Collaborate button when there are no other accepted workspace members", () => {
    renderLayout({
      canManageCollaborators: true,
      workspaceUsers: [
        {
          user_id: "collab-self",
          email: "bob@example.com",
          role: "member",
          status: "accepted",
        },
        {
          user_id: "invitee-1",
          email: "invitee@example.com",
          role: "member",
          status: "invited",
        },
      ] as any,
    });

    expect(screen.queryByTestId("collaborate-button")).not.toBeInTheDocument();
  });

  it("renders shared-chat demarcation with icon and label", () => {
    renderLayout({
      conversations: [
        {
          ...baseConversations[0],
          id: 1,
          title: "Shared with owner only",
        },
        {
          ...baseConversations[0],
          id: 2,
          title: "Shared with two others",
        },
      ],
      conversationCollaboratorsById: {
        1: ownerAndSelf,
        2: [
          ...ownerAndSelf,
          {
            user_id: "collab-2",
            display_name: "charlie@example.com",
            email: "charlie@example.com",
            role: "collaborator",
            added_at: "2026-04-25T10:02:00Z",
            added_by: "alice@example.com",
          },
          {
            user_id: "collab-3",
            display_name: "dana@example.com",
            email: "dana@example.com",
            role: "collaborator",
            added_at: "2026-04-25T10:03:00Z",
            added_by: "alice@example.com",
          },
        ],
      },
    });

    fireEvent.click(screen.getByTestId("conversation-history-button"));

    expect(
      screen.getByTestId("shared-conversation-icon-1"),
    ).toBeInTheDocument();
    expect(
      screen.getByText("Shared with alice@example.com"),
    ).toBeInTheDocument();
    expect(
      screen.getByText("Shared with alice@example.com + 2"),
    ).toBeInTheDocument();
  });

  it("renders per-turn speaker attribution using sender_user_id metadata", () => {
    renderLayout({
      messages: [
        {
          id: "message-1",
          role: "user",
          content: "Owner message",
          createdAt: new Date("2026-04-25T10:00:00Z"),
          metadata: { sender_user_id: "owner-1" },
        },
        {
          id: "message-2",
          role: "user",
          content: "Collaborator message",
          createdAt: new Date("2026-04-25T10:01:00Z"),
          metadata: { sender_user_id: "collab-2" },
        },
      ] as any,
      currentConversationCollaborators: [
        ...ownerAndSelf,
        {
          user_id: "collab-2",
          display_name: "charlie@example.com",
          email: "charlie@example.com",
          role: "collaborator",
          added_at: "2026-04-25T10:02:00Z",
          added_by: "alice@example.com",
        },
      ],
      canManageCollaborators: true,
    });

    expect(screen.getByTestId("speaker-label-message-1")).toHaveTextContent(
      "alice@example.com",
    );
    expect(screen.getByText("owner")).toBeInTheDocument();
    expect(screen.getByTestId("speaker-label-message-2")).toHaveTextContent(
      "charlie@example.com",
    );
  });

  it("renders agent names for agent-sourced sender_user_id metadata", () => {
    renderLayout({
      messages: [
        {
          id: "message-1",
          role: "user",
          content: "Agent-produced turn",
          createdAt: new Date("2026-04-25T10:03:00Z"),
          metadata: { sender_user_id: "agent:2" },
        },
      ] as any,
      conversations: [
        ...baseConversations,
        {
          ...baseConversations[0],
          id: 2,
          title: "Agent 2 conversation",
          agentName: "Calm Sparrow",
          avatar: {
            status: "assigned",
            cut: "torus",
            colour: "azure",
            reservationActive: true,
            recoveryRequired: false,
          },
        },
      ],
      canManageCollaborators: true,
    });

    expect(
      screen.getByTestId("agent-message-attribution-message-1"),
    ).toHaveTextContent("Calm Sparrow");
    const avatarSlot = screen.getByTestId(
      "agent-message-avatar-slot-message-1",
    );
    expect(
      within(avatarSlot).getByRole("img", { name: "Calm Sparrow appearance" }),
    ).toHaveAttribute("data-gemstone-cut", "torus");
    expect(
      within(
        screen.getByTestId("agent-message-attribution-message-1"),
      ).queryByRole("img"),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByTestId("agent-message-avatar-message-1-activity"),
    ).not.toBeInTheDocument();
    expect(screen.queryByText("agent:2")).not.toBeInTheDocument();
  });

  it("rehydrates persisted agent attribution after the inventory arrives", () => {
    const delegatedMessage = {
      id: "persisted-agent-message",
      role: "user",
      content: "Delegated work completed",
      createdAt: new Date("2026-04-25T10:03:00Z"),
      metadata: { sender_user_id: "agent:4694" },
    } as any;
    const sharedProps = {
      messages: [delegatedMessage],
      onSendMessage: jest.fn(),
      isLoading: false,
      onLoadConversation: jest.fn(),
      onNewConversation: jest.fn(),
      onSearchConversations: jest.fn(),
      onLoadMoreConversations: jest.fn(),
      conversationsLoading: false,
      currentConversationId: 1,
      collaborationEnabled: true,
      currentUser: currentUser as any,
      workspaceUsers: workspaceUsers as any,
      currentConversationCollaborators: ownerAndSelf,
      conversationCollaboratorsById: { 1: ownerAndSelf },
    };
    const { rerender } = render(
      <FullPageChatLayout {...sharedProps} conversations={[]} />,
    );

    expect(
      screen.getByTestId(
        "agent-message-missing-attribution-persisted-agent-message",
      ),
    ).toHaveTextContent("Agent 4694 unavailable");

    rerender(
      <FullPageChatLayout
        {...sharedProps}
        conversations={[
          {
            ...baseConversations[0],
            id: 4694,
            title: "Bright Falcon",
            agentName: "Bright Falcon",
            avatar: {
              cut: "bipyramid",
              colour: "rose",
              reservationActive: true,
              recoveryRequired: false,
            },
          },
        ]}
      />,
    );

    expect(
      screen.queryByTestId(
        "agent-message-missing-attribution-persisted-agent-message",
      ),
    ).not.toBeInTheDocument();
    expect(
      screen.getByTestId("agent-message-attribution-persisted-agent-message"),
    ).toHaveTextContent("Bright Falcon");
    const persistedAvatarSlot = screen.getByTestId(
      "agent-message-avatar-slot-persisted-agent-message",
    );
    expect(persistedAvatarSlot).toHaveClass("mmagent-chat-message__avatar");
    expect(
      within(persistedAvatarSlot).getByRole("img", {
        name: "Bright Falcon appearance",
      }),
    ).toHaveAttribute("data-gemstone-cut", "bipyramid");
    expect(
      within(persistedAvatarSlot).getByRole("img", {
        name: "Bright Falcon appearance",
      }),
    ).toHaveAttribute("data-gemstone-colour", "rose");
    expect(
      within(
        screen.getByTestId("agent-message-attribution-persisted-agent-message"),
      ).queryByRole("img"),
    ).not.toBeInTheDocument();
  });

  it("rerenders an identical persisted message when its inventory header changes", () => {
    const message = {
      id: "same-message",
      role: "assistant",
      content: "Delegated work completed",
      createdAt: new Date("2026-04-25T10:03:00Z"),
    } as any;
    const previousExtensions = { renderHeader: jest.fn() };
    const nextExtensions = { renderHeader: jest.fn() };

    expect(
      areChatMessagePropsEqual(
        { message, extensions: previousExtensions },
        { message, extensions: nextExtensions },
      ),
    ).toBe(false);
  });

  it("uses trusted sender provenance for alignment and agent attribution", () => {
    const agentConversation = {
      ...baseConversations[0],
      id: 2,
      title: "Agent 2 conversation",
      agentName: "Calm Sparrow",
      avatar: {
        cut: "torus",
        colour: "violet",
        reservationActive: true,
        recoveryRequired: false,
      },
    };

    renderLayout({
      messages: [
        {
          id: "trusted-human",
          role: "user",
          content: "My turn",
          createdAt: new Date("2026-04-25T10:00:00Z"),
          metadata: { sender_user_id: "collab-self", message_origin: "user" },
        },
        {
          id: "delegated-agent",
          role: "user",
          content: "Agent-produced turn",
          createdAt: new Date("2026-04-25T10:01:00Z"),
          metadata: { sender_user_id: "agent:2", message_origin: "agent" },
        },
        {
          id: "other-human",
          role: "user",
          content: "Other user turn",
          createdAt: new Date("2026-04-25T10:02:00Z"),
          metadata: { sender_user_id: "collab-2", message_origin: "user" },
        },
        {
          id: "assistant-turn",
          role: "assistant",
          content: "Assistant turn",
          createdAt: new Date("2026-04-25T10:03:00Z"),
          metadata: { message_origin: "user" },
        },
        {
          id: "missing-agent",
          role: "user",
          content: "Historical delegated turn",
          createdAt: new Date("2026-04-25T10:04:00Z"),
          metadata: { sender_user_id: "agent:99", message_origin: "agent" },
        },
        {
          id: "unknown-user",
          role: "user",
          content: "Unknown historical turn",
          createdAt: new Date("2026-04-25T10:05:00Z"),
          metadata: { message_origin: "user" },
        },
      ] as any,
      conversations: [...baseConversations, agentConversation],
    });

    expect(screen.getByTestId("chat-message-trusted-human")).toHaveAttribute(
      "data-presented-role",
      "user",
    );
    const currentUserAvatarSlot = screen.getByTestId(
      "agent-message-avatar-slot-trusted-human",
    );
    expect(currentUserAvatarSlot).toHaveClass("mmagent-chat-message__avatar");
    expect(
      within(currentUserAvatarSlot).getByTestId(
        "current-user-message-avatar-trusted-human",
      ),
    ).toHaveTextContent("BE");
    expect(
      within(screen.getByTestId("speaker-label-trusted-human")).queryByTestId(
        "current-user-message-avatar-trusted-human",
      ),
    ).not.toBeInTheDocument();
    for (const id of [
      "delegated-agent",
      "other-human",
      "assistant-turn",
      "missing-agent",
      "unknown-user",
    ]) {
      expect(screen.getByTestId(`chat-message-${id}`)).toHaveAttribute(
        "data-presented-role",
        "assistant",
      );
    }
    expect(
      screen.getByTestId("agent-message-attribution-delegated-agent"),
    ).toHaveTextContent("Calm Sparrow");
    const delegatedAgentAvatar = screen.getByTestId(
      "agent-message-avatar-delegated-agent",
    );
    expect(delegatedAgentAvatar).toHaveAttribute("data-gemstone-cut", "torus");
    expect(delegatedAgentAvatar).toHaveAttribute(
      "data-gemstone-colour",
      "violet",
    );
    expect(
      screen.getByTestId("agent-message-missing-attribution-missing-agent"),
    ).toHaveTextContent("Agent 99 unavailable");
    expect(
      screen.queryByTestId("agent-message-avatar-slot-missing-agent"),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByTestId("agent-message-attribution-assistant-turn"),
    ).not.toBeInTheDocument();
    for (const id of [
      "trusted-human",
      "delegated-agent",
      "other-human",
      "assistant-turn",
      "missing-agent",
      "unknown-user",
    ]) {
      expect(
        screen.queryByTestId(`generic-role-avatar-${id}`),
      ).not.toBeInTheDocument();
    }
  });

  it("keeps stamped local user provenance right-aligned with authenticated-profile initials through completion and reconciliation", () => {
    renderLayout({
      currentUser: {
        ...currentUser,
        name: undefined,
        email: "johan@metalmind.ai",
      } as any,
      messages: [
        {
          id: "1710000000000-localuser",
          role: "user",
          content: "Pending local turn",
          createdAt: new Date("2026-04-25T10:00:00Z"),
          isStreaming: true,
          metadata: { sender_user_id: "collab-self", message_origin: "user" },
        },
        {
          id: "501",
          hubMessageId: 501,
          role: "user",
          content: "Done local turn",
          createdAt: new Date("2026-04-25T10:01:00Z"),
          metadata: { sender_user_id: "collab-self", message_origin: "user" },
        },
        {
          id: "502",
          hubMessageId: 502,
          role: "user",
          content: "Reconciled local turn",
          createdAt: new Date("2026-04-25T10:02:00Z"),
          metadata: { sender_user_id: "collab-self", message_origin: "user" },
        },
        {
          id: "1710000000001-nosender",
          role: "user",
          content: "Untrusted no-sender row",
          createdAt: new Date("2026-04-25T10:03:00Z"),
        },
        {
          id: "1710000000002-agent",
          role: "user",
          content: "Agent row",
          createdAt: new Date("2026-04-25T10:04:00Z"),
          metadata: { sender_user_id: "agent:2", message_origin: "agent" },
        },
        {
          id: "1710000000003-other",
          role: "user",
          content: "Other user row",
          createdAt: new Date("2026-04-25T10:05:00Z"),
          metadata: { sender_user_id: "collab-2", message_origin: "user" },
        },
      ] as any,
    });

    for (const id of ["1710000000000-localuser", "501", "502"]) {
      expect(screen.getByTestId(`chat-message-${id}`)).toHaveAttribute(
        "data-presented-role",
        "user",
      );
      const avatarSlot = screen.getByTestId(`agent-message-avatar-slot-${id}`);
      expect(
        within(avatarSlot).getByRole("img", {
          name: "johan@metalmind.ai avatar",
        }),
      ).toHaveTextContent("J");
      expect(
        within(screen.getByTestId(`speaker-label-${id}`)).queryByRole("img"),
      ).not.toBeInTheDocument();
    }
    for (const id of [
      "1710000000001-nosender",
      "1710000000002-agent",
      "1710000000003-other",
    ]) {
      expect(screen.getByTestId(`chat-message-${id}`)).toHaveAttribute(
        "data-presented-role",
        "assistant",
      );
    }
  });

  it("uses the selected agent appearance for live, tool, and rehydrated replies while preserving delegated provenance", () => {
    const currentAgent = {
      ...baseConversations[0],
      id: 1,
      agentName: "Bright Falcon",
      avatar: {
        cut: "bipyramid",
        colour: "rose",
        reservationActive: true,
        recoveryRequired: false,
      },
    };
    const normalizedKindNullCurrentAgent = normalizeConversationSummary({
      id: currentAgent.id,
      kind: null,
      title: currentAgent.title,
      agent_name: currentAgent.agentName,
      agent_status: currentAgent.agentStatus,
      created_at: currentAgent.createdAt,
      updated_at: currentAgent.updatedAt,
      avatar: {
        cut: currentAgent.avatar.cut,
        colour: currentAgent.avatar.colour,
        reservation_active: true,
        recovery_required: false,
      },
    });
    expect(normalizedKindNullCurrentAgent?.kind).toBe("agent");
    const delegatedAgent = {
      ...baseConversations[0],
      id: 2,
      agentName: "Keen Raven",
      avatar: {
        cut: "torus",
        colour: "violet",
        reservationActive: true,
        recoveryRequired: false,
      },
    };
    const messages = [
      {
        id: "agent-live",
        role: "assistant",
        content: "Working now",
        createdAt: new Date("2026-04-25T10:00:00Z"),
        isStreaming: true,
      },
      {
        id: "agent-tool-result",
        role: "assistant",
        content: "Search completed",
        createdAt: new Date("2026-04-25T10:01:00Z"),
        toolExecutions: [
          { id: "tool-search", name: "search_chunks", status: "completed" },
        ],
      },
      {
        id: "agent-rehydrated",
        role: "assistant",
        content: "Persisted response",
        createdAt: new Date("2026-04-25T10:02:00Z"),
      },
      {
        id: "delegated-override",
        role: "assistant",
        content: "Delegated response",
        createdAt: new Date("2026-04-25T10:03:00Z"),
        metadata: { sender_user_id: "agent:2", message_origin: "agent" },
      },
      {
        id: "missing-delegated-agent",
        role: "assistant",
        content: "Historical response",
        createdAt: new Date("2026-04-25T10:04:00Z"),
        metadata: { sender_user_id: "agent:99", message_origin: "agent" },
      },
      {
        id: "other-human-no-gem",
        role: "user",
        content: "Other person",
        createdAt: new Date("2026-04-25T10:05:00Z"),
        metadata: { sender_user_id: "collab-2", message_origin: "user" },
      },
    ] as any;
    const { rerender } = renderLayout({
      workspaceMode: "agent",
      currentConversationId: 1,
      conversations: [normalizedKindNullCurrentAgent!, delegatedAgent],
      messages,
    });

    for (const id of ["agent-live", "agent-tool-result", "agent-rehydrated"]) {
      const slot = screen.getByTestId(`agent-message-avatar-slot-${id}`);
      expect(
        within(slot).getByRole("img", { name: "Bright Falcon appearance" }),
      ).toHaveAttribute("data-gemstone-colour", "rose");
      const header = screen.getByTestId(`agent-message-attribution-${id}`);
      expect(header).toHaveTextContent("Bright Falcon");
      expect(within(header).queryByRole("img")).not.toBeInTheDocument();
      const bubbleTone = screen.getByTestId(`agent-message-bubble-tone-${id}`);
      expect(bubbleTone).toHaveAttribute("data-agent-bubble-colour", "rose");
      const bubble = screen.getByTestId(`chat-message-content-${id}`);
      expect(bubble).toHaveClass("mmagent-chat-message__content");
      expect(bubble).toHaveStyle({
        backgroundColor: "hsl(324 50% 94%)",
        color: "hsl(324 50% 20%)",
      });
    }
    const toolResultBubble = screen.getByTestId(
      "chat-message-content-agent-tool-result",
    );
    expect(
      within(toolResultBubble).getByRole("link", {
        name: "search_chunks result",
      }),
    ).toBeInTheDocument();
    expect(
      within(toolResultBubble).getByText("search_chunks", { selector: "code" }),
    ).toBeInTheDocument();
    expect(
      within(toolResultBubble).getByTestId(
        "chat-message-tool-inline-agent-tool-result-tool-search",
      ),
    ).toBeInTheDocument();
    expect(
      within(
        screen.getByTestId("agent-message-avatar-slot-delegated-override"),
      ).getByRole("img", {
        name: "Keen Raven appearance",
      }),
    ).toHaveAttribute("data-gemstone-cut", "torus");
    expect(
      screen.getByTestId("agent-message-attribution-delegated-override"),
    ).toHaveTextContent("Keen Raven");
    expect(
      screen.getByTestId("agent-message-bubble-tone-delegated-override"),
    ).toHaveAttribute("data-agent-bubble-colour", "violet");
    expect(
      screen.getByTestId("chat-message-content-delegated-override"),
    ).toHaveStyle({
      backgroundColor: "hsl(268 52% 94%)",
      color: "hsl(268 52% 20%)",
    });
    expect(
      screen.queryByTestId("agent-message-avatar-slot-missing-delegated-agent"),
    ).not.toBeInTheDocument();
    expect(
      screen.getByTestId(
        "agent-message-missing-attribution-missing-delegated-agent",
      ),
    ).toHaveTextContent("Agent 99 unavailable");
    expect(
      screen.queryByTestId("agent-message-avatar-slot-other-human-no-gem"),
    ).not.toBeInTheDocument();

    rerender(
      <FullPageChatLayout
        messages={messages}
        onSendMessage={jest.fn()}
        isLoading={false}
        conversations={[normalizedKindNullCurrentAgent!, delegatedAgent]}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={2}
        workspaceMode="agent"
        collaborationEnabled={true}
        currentUser={currentUser as any}
        workspaceUsers={workspaceUsers as any}
        currentConversationCollaborators={ownerAndSelf}
        conversationCollaboratorsById={{ 1: ownerAndSelf, 2: ownerAndSelf }}
      />,
    );

    expect(
      within(
        screen.getByTestId("agent-message-avatar-slot-agent-rehydrated"),
      ).getByRole("img", {
        name: "Keen Raven appearance",
      }),
    ).toBeInTheDocument();
    expect(
      screen.getByTestId("agent-message-attribution-agent-rehydrated"),
    ).toHaveTextContent("Keen Raven");
    expect(
      screen.getByTestId("agent-message-attribution-delegated-override"),
    ).toHaveTextContent("Keen Raven");

    const refreshedSelectedAgent = {
      ...delegatedAgent,
      avatar: {
        ...delegatedAgent.avatar,
        colour: "moss",
      },
    };
    rerender(
      <FullPageChatLayout
        messages={messages}
        onSendMessage={jest.fn()}
        isLoading={false}
        conversations={[
          normalizedKindNullCurrentAgent!,
          refreshedSelectedAgent,
        ]}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={2}
        workspaceMode="agent"
        collaborationEnabled={true}
        currentUser={currentUser as any}
        workspaceUsers={workspaceUsers as any}
        currentConversationCollaborators={ownerAndSelf}
        conversationCollaboratorsById={{ 1: ownerAndSelf, 2: ownerAndSelf }}
      />,
    );

    expect(
      within(
        screen.getByTestId("agent-message-avatar-slot-agent-rehydrated"),
      ).getByRole("img", {
        name: "Keen Raven appearance",
      }),
    ).toHaveAttribute("data-gemstone-colour", "moss");
    expect(
      screen.getByTestId("agent-message-bubble-tone-agent-rehydrated"),
    ).toHaveAttribute("data-agent-bubble-colour", "moss");
    expect(
      screen.getByTestId("chat-message-content-agent-rehydrated"),
    ).toHaveStyle({
      backgroundColor: "hsl(102 50% 94%)",
      color: "hsl(102 50% 20%)",
    });
  });
  it("restores existing file attachments when editing a user message", async () => {
    const onDeleteMessagesFrom = jest.fn().mockResolvedValue(undefined);
    const onSendMessageWithFiles = jest.fn().mockResolvedValue(undefined);
    const onRestoreMessageFiles = jest
      .fn()
      .mockResolvedValue([
        new File(["image-bytes"], "screenshot.png", { type: "image/png" }),
      ]);

    renderLayout({
      messages: [
        {
          id: "message-with-file",
          role: "user",
          content: "Please update this",
          createdAt: new Date("2026-04-25T10:00:00Z"),
          metadata: { sender_user_id: "collab-self", message_origin: "user" },
          fileAttachments: [
            {
              filename: "screenshot.png",
              fileSize: 1234,
              mimeType: "image/png",
              docId: "ws/doc-1",
            },
          ],
        },
      ] as any,
      onDeleteMessagesFrom,
      onSendMessageWithFiles,
      onRestoreMessageFiles,
    });

    fireEvent.click(screen.getByTitle("Edit message"));

    expect(onRestoreMessageFiles).toHaveBeenCalledTimes(1);
    expect(await screen.findByText("screenshot.png")).toBeInTheDocument();

    const textbox = screen.getByRole("textbox");
    fireEvent.change(textbox, { target: { value: "Please update this now" } });
    fireEvent.keyDown(textbox, {
      key: "Enter",
      code: "Enter",
      shiftKey: false,
    });

    await waitFor(() => {
      expect(onDeleteMessagesFrom).toHaveBeenCalledTimes(1);
    });
    await waitFor(() => {
      expect(onSendMessageWithFiles).toHaveBeenCalledTimes(1);
    });

    expect(onSendMessageWithFiles.mock.calls[0][0]).toBe(
      "Please update this now",
    );
    expect(onSendMessageWithFiles.mock.calls[0][1]).toHaveLength(1);
    expect(onSendMessageWithFiles.mock.calls[0][1][0].name).toBe(
      "screenshot.png",
    );
  });

  it("submits an owner assignment as a complete cut and colour pair", async () => {
    const onAssignAgentAvatar = jest.fn().mockResolvedValue(undefined);
    renderLayout({
      workspaceMode: "agent",
      onAssignAgentAvatar,
      conversations: [
        {
          ...baseConversations[0],
          userRole: "owner",
          avatar: {
            cut: "rhomboid",
            colour: "azure",
            reservationActive: false,
            recoveryRequired: true,
          },
        },
      ],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /change appearance/i }),
    );
    await waitFor(() =>
      expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
    );
    fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));

    await waitFor(() =>
      expect(onAssignAgentAvatar).toHaveBeenCalledWith(1, {
        selected_cut: "rhomboid",
        selected_colour: "azure",
      }),
    );
  });

  it("filters other agents' independent reservations while retaining this agent's pair", async () => {
    const onGetAgentAvatarReservations = jest.fn().mockResolvedValue({
      reservedCuts: ["brilliant"],
      reservedColours: ["blue"],
    });
    const onAssignAgentAvatar = jest.fn().mockResolvedValue(undefined);
    renderLayout({
      workspaceMode: "agent",
      onGetAgentAvatarReservations,
      onAssignAgentAvatar,
      conversations: [
        {
          ...baseConversations[0],
          userRole: "owner",
          avatar: {
            cut: "rhomboid",
            colour: "azure",
            reservationActive: true,
            recoveryRequired: false,
          },
        },
      ],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /change appearance/i }),
    );
    await waitFor(() =>
      expect(onGetAgentAvatarReservations).toHaveBeenCalledWith(1),
    );
    await waitFor(() =>
      expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
    );

    fireEvent.click(screen.getByRole("combobox", { name: "Cut" }));
    const selfCut = await screen.findByRole("option", { name: "rhomboid" });
    expect(selfCut).toBeEnabled();
    expect(
      screen.queryByRole("option", { name: "brilliant" }),
    ).not.toBeInTheDocument();
    fireEvent.click(selfCut);

    fireEvent.click(screen.getByRole("combobox", { name: "Colour" }));
    const selfColour = await screen.findByRole("option", { name: "azure" });
    expect(selfColour).toBeEnabled();
    expect(
      screen.queryByRole("option", { name: "blue" }),
    ).not.toBeInTheDocument();
    fireEvent.click(selfColour);

    fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));
    await waitFor(() =>
      expect(onAssignAgentAvatar).toHaveBeenCalledWith(1, {
        selected_cut: "rhomboid",
        selected_colour: "azure",
      }),
    );
    await waitFor(() =>
      expect(onGetAgentAvatarReservations).toHaveBeenCalledTimes(2),
    );
  });

  it("refreshes reservations after a 409 and disables assignment when either palette is exhausted", async () => {
    const onGetAgentAvatarReservations = jest
      .fn()
      .mockResolvedValueOnce({ reservedCuts: [], reservedColours: [] })
      .mockResolvedValueOnce({
        reservedCuts: [...AGENT_AVATAR_CUTS],
        reservedColours: [],
      });
    const onAssignAgentAvatar = jest.fn().mockRejectedValue(
      Object.assign(new Error("HTTP Error: status: 409 (Conflict)"), {
        status: 409,
      }),
    );
    renderLayout({
      workspaceMode: "agent",
      onGetAgentAvatarReservations,
      onAssignAgentAvatar,
      conversations: [{ ...baseConversations[0], userRole: "owner" }],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /change appearance/i }),
    );
    await waitFor(() =>
      expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
    );
    fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));

    expect(
      await screen.findByText(
        "That cut or colour is unavailable. Choose another pair.",
      ),
    ).toBeInTheDocument();
    await waitFor(() =>
      expect(onGetAgentAvatarReservations).toHaveBeenCalledTimes(2),
    );
    expect(
      await screen.findByText(
        "No appearance values are available in this workspace.",
      ),
    ).toBeInTheDocument();
    expect(screen.getByRole("button", { name: /^assign$/i })).toBeDisabled();
  });

  it("clears a selected value that becomes reserved in a 409 race refresh", async () => {
    const onGetAgentAvatarReservations = jest
      .fn()
      .mockResolvedValueOnce({ reservedCuts: [], reservedColours: [] })
      .mockResolvedValueOnce({ reservedCuts: ["torus"], reservedColours: [] });
    const onAssignAgentAvatar = jest.fn().mockRejectedValue(
      Object.assign(new Error("HTTP Error: status: 409 (Conflict)"), {
        status: 409,
      }),
    );
    renderLayout({
      workspaceMode: "agent",
      onGetAgentAvatarReservations,
      onAssignAgentAvatar,
      conversations: [{ ...baseConversations[0], userRole: "owner" }],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /change appearance/i }),
    );
    await waitFor(() =>
      expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
    );
    fireEvent.click(screen.getByRole("combobox", { name: "Cut" }));
    fireEvent.click(await screen.findByRole("option", { name: "torus" }));
    expect(screen.getByTestId("agent-avatar-picker-preview")).toHaveAttribute(
      "data-gemstone-cut",
      "torus",
    );
    fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));

    await waitFor(() =>
      expect(onGetAgentAvatarReservations).toHaveBeenCalledTimes(2),
    );
    await waitFor(() =>
      expect(
        screen.getByTestId("agent-avatar-picker-preview"),
      ).not.toHaveAttribute("data-gemstone-cut", "torus"),
    );
  });

  it("offers release only to a decommissioned owner and awaits it", async () => {
    const onReleaseAgentAvatar = jest.fn().mockResolvedValue(undefined);
    renderLayout({
      workspaceMode: "agent",
      onReleaseAgentAvatar,
      conversations: [
        {
          ...baseConversations[0],
          userRole: "owner",
          decommissionedAt: "2026-09-09T12:00:00Z",
        },
      ],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /release appearance/i }),
    );
    await waitFor(() => expect(onReleaseAgentAvatar).toHaveBeenCalledWith(1));
  });

  it("keeps the picker open after a 409 so the refetched authoritative list can choose another pair", async () => {
    const onAssignAgentAvatar = jest.fn().mockRejectedValue(
      Object.assign(new Error("HTTP Error: status: 409 (Conflict)"), {
        status: 409,
      }),
    );
    renderLayout({
      workspaceMode: "agent",
      onAssignAgentAvatar,
      conversations: [
        {
          ...baseConversations[0],
          userRole: "owner",
          avatar: {
            cut: "rhomboid",
            colour: "azure",
            reservationActive: false,
            recoveryRequired: true,
          },
        },
      ],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /change appearance/i }),
    );
    await waitFor(() =>
      expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
    );
    fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));

    expect(
      await screen.findByText(
        "That cut or colour is unavailable. Choose another pair.",
      ),
    ).toBeInTheDocument();
    expect(
      screen.getByRole("dialog", { name: /choose agent appearance/i }),
    ).toBeInTheDocument();
  });

  it("distinguishes an in-progress appearance update from a palette collision", async () => {
    const onAssignAgentAvatar = jest
      .fn()
      .mockRejectedValue(
        Object.assign(new Error("appearance update busy"), { status: 409 }),
      );
    renderLayout({
      workspaceMode: "agent",
      onAssignAgentAvatar,
      conversations: [{ ...baseConversations[0], userRole: "owner" }],
    });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /change appearance/i }),
    );
    await waitFor(() =>
      expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
    );
    fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));

    expect(
      await screen.findByText(
        "This agent appearance is being updated. Try again.",
      ),
    ).toBeInTheDocument();
  });

  it.each([403, 422, 500])(
    "keeps non-conflict appearance failures generic (%i)",
    async (status) => {
      const onAssignAgentAvatar = jest
        .fn()
        .mockRejectedValue(
          Object.assign(new Error(`HTTP Error: status: ${status}`), { status }),
        );
      renderLayout({
        workspaceMode: "agent",
        onAssignAgentAvatar,
        conversations: [{ ...baseConversations[0], userRole: "owner" }],
      });

      fireEvent.click(
        screen.getByTestId("agent-inventory-agent-1-menu-trigger"),
      );
      fireEvent.click(
        await screen.findByRole("menuitem", { name: /change appearance/i }),
      );
      await waitFor(() =>
        expect(screen.getByRole("button", { name: /^assign$/i })).toBeEnabled(),
      );
      fireEvent.click(screen.getByRole("button", { name: /^assign$/i }));

      expect(
        await screen.findByText(
          "Appearance could not be assigned. The list was refreshed.",
        ),
      ).toBeInTheDocument();
      expect(
        screen.getByRole("dialog", { name: /choose agent appearance/i }),
      ).toBeInTheDocument();
    },
  );

  it("renders a 150-agent roster and attributes a persisted message from a later agent", () => {
    const conversations = Array.from({ length: 150 }, (_, index) => ({
      ...baseConversations[0],
      id: index + 1,
      agentName: `Agent ${index + 1}`,
      createdAt: new Date(Date.UTC(2026, 0, 1, 0, index)).toISOString(),
      avatar: {
        cut: index === 16 ? "spire" : index % 2 === 0 ? "brilliant" : "spire",
        colour: index === 16 ? "amber" : index % 2 === 0 ? "blue" : "amber",
        reservationActive: true,
        recoveryRequired: false,
      },
    }));
    renderLayout({
      workspaceMode: "agent",
      conversations,
      messages: [
        {
          id: "agent-17-message",
          role: "assistant",
          content: "Reply",
          createdAt: new Date(),
          metadata: { sender_user_id: "agent:17" },
        },
      ] as any,
    });

    expect(screen.getByTestId("agent-inventory-agent-150")).toBeInTheDocument();
    expect(screen.getAllByTestId(/agent-gemstone-\d+$/)).toHaveLength(150);
    const laterAgentAvatar = screen.getByTestId(
      "agent-message-avatar-agent-17-message",
    );
    expect(laterAgentAvatar).toHaveAttribute("data-gemstone-cut", "spire");
    expect(laterAgentAvatar).toHaveAttribute("data-gemstone-colour", "amber");
  });

  it("loads the next roster page when the agent strip reaches its end", () => {
    const firstPage = Array.from({ length: 100 }, (_, index) => ({
      ...baseConversations[0],
      id: index + 1,
      agentName: `Agent ${index + 1}`,
      createdAt: new Date(Date.UTC(2026, 0, 1, 0, index)).toISOString(),
      avatar: {
        cut: "brilliant",
        colour: "blue",
        reservationActive: true,
        recoveryRequired: false,
      },
    }));
    const nextPage = Array.from({ length: 50 }, (_, index) => ({
      ...baseConversations[0],
      id: index + 101,
      agentName: `Agent ${index + 101}`,
      createdAt: new Date(Date.UTC(2026, 0, 2, 0, index)).toISOString(),
      avatar: {
        cut: "spire",
        colour: "amber",
        reservationActive: true,
        recoveryRequired: false,
      },
    }));
    let loadMoreCalls = 0;

    const PaginatedRoster = () => {
      const [conversations, setConversations] = React.useState(firstPage);
      const [hasMoreConversations, setHasMoreConversations] =
        React.useState(true);
      const [conversationsLoading, setConversationsLoading] =
        React.useState(false);
      const loadMore = () => {
        loadMoreCalls += 1;
        setConversationsLoading(true);
        setConversations((current) => [...current, ...nextPage]);
        setHasMoreConversations(false);
        setConversationsLoading(false);
      };

      return (
        <FullPageChatLayout
          messages={[]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={conversations}
          onLoadConversation={jest.fn()}
          onNewConversation={jest.fn()}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={loadMore}
          hasMoreConversations={hasMoreConversations}
          conversationsLoading={conversationsLoading}
          currentConversationId={1}
          workspaceMode="agent"
        />
      );
    };

    render(<PaginatedRoster />);
    const inventory = screen.getByTestId("agent-inventory-scroll");
    Object.defineProperties(inventory, {
      scrollWidth: { configurable: true, value: 5000 },
      clientWidth: { configurable: true, value: 1000 },
      scrollLeft: { configurable: true, writable: true, value: 3500 },
    });

    fireEvent.scroll(inventory);
    expect(loadMoreCalls).toBe(0);

    (inventory as HTMLElement).scrollLeft = 4000;
    fireEvent.scroll(inventory);

    expect(loadMoreCalls).toBe(1);
    expect(screen.getByTestId("agent-inventory-agent-150")).toBeInTheDocument();
    expect(screen.getAllByTestId(/agent-gemstone-\d+$/)).toHaveLength(150);

    fireEvent.scroll(inventory);
    expect(loadMoreCalls).toBe(1);
  });

  it("shows a retry when a roster page fails, then appends it after retry succeeds", async () => {
    const firstPage = Array.from({ length: 5 }, (_, index) => ({
      ...baseConversations[0],
      id: index + 1,
      agentName: `Agent ${index + 1}`,
      createdAt: new Date(Date.UTC(2026, 0, 1, 0, index)).toISOString(),
      avatar: {
        cut: "brilliant",
        colour: "blue",
        reservationActive: true,
        recoveryRequired: false,
      },
    }));
    const secondPage = Array.from({ length: 5 }, (_, index) => ({
      ...baseConversations[0],
      id: index + 6,
      agentName: `Agent ${index + 6}`,
      createdAt: new Date(Date.UTC(2026, 0, 2, 0, index)).toISOString(),
      avatar: {
        cut: "spire",
        colour: "amber",
        reservationActive: true,
        recoveryRequired: false,
      },
    }));
    let attempts = 0;

    const PaginatedRoster = () => {
      const [conversations, setConversations] = React.useState(firstPage);
      const [hasMoreConversations, setHasMoreConversations] =
        React.useState(true);
      const loadMore = () => {
        attempts += 1;
        if (attempts === 1) return Promise.resolve(false);
        setConversations((current) => [...current, ...secondPage]);
        setHasMoreConversations(false);
        return Promise.resolve(true);
      };

      return (
        <FullPageChatLayout
          messages={[]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={conversations}
          onLoadConversation={jest.fn()}
          onNewConversation={jest.fn()}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={loadMore}
          hasMoreConversations={hasMoreConversations}
          conversationsLoading={false}
          currentConversationId={1}
          workspaceMode="agent"
        />
      );
    };

    render(<PaginatedRoster />);
    const inventory = screen.getByTestId("agent-inventory-scroll");
    Object.defineProperties(inventory, {
      scrollWidth: { configurable: true, value: 5000 },
      clientWidth: { configurable: true, value: 1000 },
      scrollLeft: { configurable: true, writable: true, value: 4000 },
    });

    fireEvent.scroll(inventory);
    expect(
      await screen.findByTestId("agent-inventory-load-more-error"),
    ).toHaveTextContent("Couldn't load more agents. Retry");
    expect(screen.getAllByTestId(/agent-gemstone-\d+$/)).toHaveLength(5);

    fireEvent.click(screen.getByTestId("agent-inventory-load-more-error"));
    await waitFor(() => {
      expect(screen.getAllByTestId(/agent-gemstone-\d+$/)).toHaveLength(10);
    });
    expect(
      screen.queryByTestId("agent-inventory-load-more-error"),
    ).not.toBeInTheDocument();
    expect(screen.getByTestId("agent-inventory-agent-10")).toBeInTheDocument();
    expect(attempts).toBe(2);
  });

  it("keeps a mixed normal and legacy-fallback inventory compact at populated responsive widths", () => {
    const conversations = Array.from({ length: 16 }, (_, index) => ({
      ...baseConversations[0],
      id: index + 1,
      agentName: `Agent ${index + 1}`,
      createdAt: new Date(Date.UTC(2026, 0, 1, 0, index)).toISOString(),
      avatar: {
        cut: index % 2 === 0 ? "brilliant" : "spire",
        colour: index % 2 === 0 ? "blue" : "amber",
        reservationActive: index % 3 !== 0,
        recoveryRequired: index % 3 === 0,
      },
    }));
    renderLayout({ workspaceMode: "agent", conversations });

    // The strip deliberately scrolls rather than shrinking/clipping the
    // roster on a narrow viewport.
    expect(screen.getByTestId("agent-inventory-scroll")).toHaveStyle({
      overflowX: "auto",
      overflowY: "hidden",
    });
    expect(screen.getByTestId("agent-inventory-agent-16")).toBeInTheDocument();
    expect(screen.getAllByTestId(/agent-gemstone-\d+$/)).toHaveLength(16);

    // Legacy/no-row agents remain normal usable gemstones. Their recovery
    // provenance stays internal; the populated strip contains no warning
    // copy, badges, or icon-only state that could crowd neighboring tiles.
    expect(
      screen.queryByText("Appearance recovery needed"),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByTestId("agent-avatar-recovery-1"),
    ).not.toBeInTheDocument();
    expect(
      screen.getByTestId("agent-inventory-agent-1-menu-trigger"),
    ).toBeEnabled();
  });

  it("places compact initials below selected and unselected gemstones at desktop and coarse widths", async () => {
    const assertInitialsGeometry = (
      id: number,
      expected: {
        width: number;
        height: number;
        avatar: number;
        initials: string;
      },
    ) => {
      const tile = screen.getByTestId(`agent-inventory-tile-${id}`);
      const initials = screen.getByTestId(`agent-inventory-initials-${id}`);
      const tileHeight = Number(tile.getAttribute("data-tile-height"));
      const avatarSize = Number(tile.getAttribute("data-avatar-size"));
      const initialsSize = Number(tile.getAttribute("data-initials-size"));
      const initialsGap = Number(tile.getAttribute("data-initials-gap"));

      expect(tile).toHaveAttribute("data-tile-width", String(expected.width));
      expect(tileHeight).toBe(expected.height);
      expect(avatarSize).toBe(expected.avatar);
      expect(initials).toHaveTextContent(expected.initials);
      // The status indication sits behind the stone rather than consuming a
      // label row. Initials remain inside the compact tile below the avatar.
      const avatarBottom = 4 + avatarSize;
      const initialsTop = avatarBottom + initialsGap;
      expect(initialsTop).toBeGreaterThan(avatarBottom);
      expect(initialsTop + initialsSize).toBeLessThanOrEqual(tileHeight);
    };

    const view = renderLayout({
      workspaceMode: "agent",
      currentConversationId: 31,
      conversations: [
        { ...baseConversations[0], id: 31, agentName: "Ada Lovelace" },
        { ...baseConversations[0], id: 32, agentName: "Q" },
        {
          ...baseConversations[0],
          id: 33,
          agentName: "Citation Verification Analyst",
        },
      ],
    });

    assertInitialsGeometry(31, {
      width: 64,
      height: 62,
      avatar: 38,
      initials: "AL",
    });
    assertInitialsGeometry(32, {
      width: 64,
      height: 62,
      avatar: 38,
      initials: "Q",
    });
    assertInitialsGeometry(33, {
      width: 64,
      height: 62,
      avatar: 38,
      initials: "CA",
    });
    expect(
      screen.queryByTestId("agent-inventory-activity-31"),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByTestId("selected-agent-activity"),
    ).not.toBeInTheDocument();
    expect(screen.getByTestId("agent-inventory-initials-32")).toHaveAttribute(
      "aria-label",
      "Q initials",
    );

    view.unmount();
    const matchMedia = window.matchMedia;
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: true })),
    });
    renderLayout({
      workspaceMode: "agent",
      currentConversationId: 31,
      conversations: [
        { ...baseConversations[0], id: 31, agentName: "Ada Lovelace" },
        { ...baseConversations[0], id: 32, agentName: "Q" },
        {
          ...baseConversations[0],
          id: 33,
          agentName: "Citation Verification Analyst",
        },
      ],
    });

    await waitFor(() =>
      expect(screen.getByTestId("agent-inventory-tile-31")).toHaveAttribute(
        "data-tile-width",
        "76",
      ),
    );
    assertInitialsGeometry(31, {
      width: 76,
      height: 70,
      avatar: 42,
      initials: "AL",
    });
    assertInitialsGeometry(32, {
      width: 76,
      height: 70,
      avatar: 42,
      initials: "Q",
    });
    assertInitialsGeometry(33, {
      width: 76,
      height: 70,
      avatar: 42,
      initials: "CA",
    });
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
  });

  it("expires the shared finished activity clock in the strip without polling", () => {
    jest.useFakeTimers();
    jest.setSystemTime(new Date("2026-09-10T10:00:00Z"));
    const finishedAt = new Date(Date.now()).toISOString();
    renderLayout({
      workspaceMode: "agent",
      currentConversationId: 31,
      conversations: [
        {
          ...baseConversations[0],
          id: 31,
          agentName: "Clock Agent",
          agentStatus: "finished",
          agentLastHeartbeatAt: finishedAt,
        },
      ],
    });

    expect(
      screen.queryByTestId("agent-inventory-activity-31"),
    ).not.toBeInTheDocument();
    expect(screen.getByTestId("agent-gemstone-31")).toHaveAttribute(
      "data-gemstone-status-circle",
      "finished",
    );

    act(() => jest.advanceTimersByTime(300_000));

    expect(screen.getByTestId("agent-gemstone-31")).toHaveAttribute(
      "data-gemstone-status-circle",
      "none",
    );
    jest.useRealTimers();
  });

  it("opens the exact conversation ID carried by each inventory tile", () => {
    const onLoadConversation = jest.fn();
    renderLayout({
      workspaceMode: "agent",
      currentConversationId: 7244,
      onLoadConversation,
      conversations: [
        {
          ...baseConversations[0],
          id: 7244,
          agentName: "Earlier agent",
          createdAt: "2026-09-09T08:00:00Z",
        },
        {
          ...baseConversations[0],
          id: 7245,
          agentName: "Citation Verification Analyst",
          createdAt: "2026-09-09T08:01:00Z",
        },
      ],
    });

    expect(
      screen.getByTestId("agent-inventory-agent-7245"),
    ).toHaveAccessibleName("Open Citation Verification Analyst");
    fireEvent.click(screen.getByTestId("agent-inventory-agent-7245"));
    expect(onLoadConversation).toHaveBeenCalledWith(7245);
    expect(onLoadConversation).not.toHaveBeenCalledWith(7244);
  });

  it("blocks sending while a conversation switch is in flight, then sends after it settles", () => {
    const onSendMessage = jest.fn();
    const { rerender } = renderLayout({
      workspaceMode: "agent",
      onSendMessage,
      conversationSwitching: true,
    });
    const input = screen.getByRole("textbox");
    fireEvent.change(input, { target: { value: "Use this conversation" } });
    expect(screen.getByRole("button", { name: "Send message" })).toBeDisabled();
    expect(screen.getByTestId("agent-inventory-agent-1")).toBeDisabled();
    expect(
      screen.getByTestId("agent-inventory-agent-1-menu-trigger"),
    ).toBeDisabled();
    fireEvent.keyDown(input, { key: "Enter", code: "Enter" });
    expect(onSendMessage).not.toHaveBeenCalled();

    rerender(
      <FullPageChatLayout
        messages={[]}
        onSendMessage={onSendMessage}
        isLoading={false}
        conversations={baseConversations}
        onLoadConversation={jest.fn()}
        onNewConversation={jest.fn()}
        onSearchConversations={jest.fn()}
        onLoadMoreConversations={jest.fn()}
        conversationsLoading={false}
        currentConversationId={1}
        workspaceMode="agent"
      />,
    );
    fireEvent.change(screen.getByRole("textbox"), {
      target: { value: "Use this conversation" },
    });
    fireEvent.click(screen.getByRole("button", { name: "Send message" }));
    expect(onSendMessage).toHaveBeenCalledWith("Use this conversation");
  });

  it("reenables memoized agent inventory controls after a conversation switch settles", () => {
    const onLoadConversation = jest.fn();
    const onNewConversation = jest.fn();
    const onSearchConversations = jest.fn();
    const onLoadMoreConversations = jest.fn();
    const props = {
      messages: [] as any[],
      onSendMessage: jest.fn(),
      isLoading: false,
      conversations: baseConversations,
      onLoadConversation,
      onNewConversation,
      onSearchConversations,
      onLoadMoreConversations,
      conversationsLoading: false,
      currentConversationId: 1,
      workspaceMode: "agent" as const,
      workspaceRole: "owner",
    };
    const { rerender } = render(
      <FullPageChatLayout {...props} conversationSwitching={true} />,
    );

    expect(screen.getByTestId("agent-inventory-agent-1")).toBeDisabled();
    expect(
      screen.getByTestId("agent-inventory-agent-1-menu-trigger"),
    ).toBeDisabled();

    rerender(<FullPageChatLayout {...props} conversationSwitching={false} />);

    const tile = screen.getByTestId("agent-inventory-agent-1");
    const trigger = screen.getByTestId("agent-inventory-agent-1-menu-trigger");
    expect(tile).toBeEnabled();
    expect(trigger).toBeEnabled();
    fireEvent.click(tile);
    expect(onLoadConversation).toHaveBeenCalledWith(1);
    fireEvent.click(trigger);
    expect(
      screen.getByRole("menuitem", { name: /edit persona/i }),
    ).toBeInTheDocument();
  });

  it("does not animate a gemstone when reduced motion is requested", () => {
    const matchMedia = window.matchMedia;
    const requestAnimationFrame = window.requestAnimationFrame;
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: true })),
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: jest.fn(),
    });
    render(
      <AgentGemstone
        id={7}
        cut="torus"
        colour="azure"
        testId="reduced-motion-gemstone"
      />,
    );
    expect(window.requestAnimationFrame).not.toHaveBeenCalled();
    expect(screen.getByTestId("reduced-motion-gemstone")).toHaveAttribute(
      "data-gemstone-cut",
      "torus",
    );
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: requestAnimationFrame,
    });
  });

  it("awaits a failed delete, retains the agent, and exposes Retry delete", async () => {
    const onDeleteConversation = jest
      .fn()
      .mockRejectedValue(new Error("Hub delete failed"));
    renderLayout({ workspaceMode: "agent", onDeleteConversation });

    fireEvent.click(screen.getByTestId("agent-inventory-agent-1-menu-trigger"));
    fireEvent.click(
      await screen.findByRole("menuitem", { name: /delete agent/i }),
    );
    fireEvent.click(screen.getByRole("button", { name: /^delete$/i }));

    expect(
      await screen.findByText(/delete did not complete.*remains visible/i),
    ).toBeInTheDocument();
    expect(onDeleteConversation).toHaveBeenCalledWith(1);
    expect(screen.getByTestId("agent-inventory-agent-1")).toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: /retry delete/i }),
    ).toBeInTheDocument();
  });
});
